import { L10nBase } from '@skylab-shared/core';
import { ToastDelay } from '@skylab-shared/ui';

import { NgClass } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, EventEmitter, HostListener, Inject, Input, Output, ViewChild } from '@angular/core';
import { L10n } from '@skylab-angular-shared/core';
import { TranslatePipe } from '@skylab-angular-shared/pipes';
import { ToastService } from '../toast/toast.service';
import { TooltipDirective } from '../tooltip/tooltip.directive';
import { IconComponent } from '../icon/icon.component';

export const enum FileSizeUnit {
	GB = 'GB',
	MB = 'MB',
	KB = 'KB',
}

export const BASE_UNIT_VALUE = 1024;
export const BYTES_PER_MB = BASE_UNIT_VALUE * BASE_UNIT_VALUE;
export const BYTES_PER_GB = BYTES_PER_MB * BASE_UNIT_VALUE;

export const calculateFileSize = (size: number, unit: FileSizeUnit) => {
	if (!unit) return 0;

	return (<Record<FileSizeUnit, number>>{
		[FileSizeUnit.KB]: BASE_UNIT_VALUE,
		[FileSizeUnit.MB]: BYTES_PER_MB,
		[FileSizeUnit.GB]: BYTES_PER_GB
	})[unit] * size;
}

@Component({
	selector: 'skl-filepicker-ins',
	templateUrl: './filepicker.component-ins.html',
	schemas: [CUSTOM_ELEMENTS_SCHEMA],
	imports: [NgClass, TooltipDirective, TranslatePipe, IconComponent]
})
export class FilepickerInsComponent {
	@Input() public accept: string | null = null;
	@Input() public maxSize: number | null = null;
	@Input() public placeholder: string | null = null;

	@Input() public fileName = '';
	@Input() public unit = FileSizeUnit.MB;

	@Output() public onPick = new EventEmitter<File>();
	@Output() public onClear = new EventEmitter<void>();

	@ViewChild('filepicker', {static: true}) public filepickerIns!: ElementRef<HTMLInsFilepickerElement>;

	public constructor(
		private readonly toastService: ToastService,
		@Inject(L10n) private readonly l10n: L10nBase
	) { }

	@HostListener('insChange', ['$event'])
	protected handleInsChange(event: Event): void {
		const customEvent = event as CustomEvent<any>;

		const file: File = customEvent.detail.files[0]; // ATM it is not necessary to handle multifile
		if (!file) return;

		if (this.maxSize && (file.size > calculateFileSize(this.maxSize, this.unit))) {
			this.toastService.showAsync({
				type: 'error',
				text: this.l10n._T('ui.file-picker.error', {fileName: file.name}),
				duration: ToastDelay.Short,
			});
			return;
		}

		this.fileName = file.name;

		this.onPick.emit(file);
	}

	public handlePick(): void {
		this.filepickerIns.nativeElement.openFilePick();
	}

	public handleClear(): void {
		this.fileName = '';
		this.onClear.emit();
	}
}
